import { useState } from "react";
import { motion } from "framer-motion";
import { Clock, Loader2, Mail, MapPin, Navigation, Phone, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useFormGuard } from "@/lib/formGuard";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { MessengerIcon, WhatsAppIcon, messengerLink, waLink } from "./SocialLinks";
import { FieldError, errCls, focusFirstError, isEmail, isPhone } from "@/lib/validate";

export default function Contact({ content }) {
  const { t, lang } = useT();
  const { guard, refresh: refreshGuard, Honeypot, GuardNotice } = useFormGuard();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target ? e.target.value : e });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = t.errors.required;
    if (!form.email.trim()) er.email = t.errors.required;
    else if (!isEmail(form.email)) er.email = t.errors.email;
    if (form.phone.trim() && !isPhone(form.phone)) er.phone = t.errors.phone;
    if (!form.message.trim()) er.message = t.errors.required;
    else if (form.message.trim().length < 10) er.message = t.errors.message;
    if (!consent) er.consent = t.errors.consent;
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error(t.errors.summary);
      focusFirstError(er);
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { ...form, ...(await guard()) });
      toast.success(t.contact.toast_ok);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setConsent(false);
      setErrors({});
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      toast.error(status === 429 ? t.errors.too_many : typeof detail === "string" ? detail : t.contact.toast_err);
      if (status === 400) refreshGuard();
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: Phone, label: t.contact.labels.phone, value: content.contact_phone, href: `tel:${content.contact_phone.replace(/\s/g, "")}`, testid: "contact-info-phone" },
    { icon: Smartphone, label: t.contact.labels.mobile, value: content.contact_mobile, href: `tel:${content.contact_mobile.replace(/\s/g, "")}`, testid: "contact-info-mobile" },
    { icon: Mail, label: t.contact.labels.email, value: content.contact_email, href: `mailto:${content.contact_email}`, testid: "contact-info-email" },
    { icon: MapPin, label: t.contact.labels.location, value: content.contact_address, testid: "contact-info-address" },
    { icon: Clock, label: t.contact.labels.hours, value: content.contact_hours, testid: "contact-info-hours" },
  ];

  return (
    <section id="biuro-wirtualne-kontakt" data-testid="contact-section" className="py-16 sm:py-24 lg:py-32 bg-slate-900 dark:bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400 font-semibold"
          >
            <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-yellow mr-3 align-middle" />{t.contact.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-4"
          >
            {t.contact.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="text-slate-400 text-base leading-relaxed mt-4"
          >
            {t.contact.sub}
          </motion.p>

          <div className="mt-10 space-y-5">
            {info.map((item, i) => (
              <motion.div
                key={item.testid}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.08, duration: 0.5 }}
                data-testid={item.testid}
                className="flex items-center gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-500/10 text-slate-400 flex items-center justify-center shrink-0">
                  <item.icon size={17} />
                </div>
                <div>
                  <div className="text-xs text-slate-500">{item.label}</div>
                  {item.href ? (
                    <a href={item.href} className="text-sm font-semibold hover:text-slate-300 transition-colors">{item.value}</a>
                  ) : (
                    <div className="text-sm font-semibold">{item.value}</div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {content.contact_whatsapp && (
              <a data-testid="contact-whatsapp-link" href={waLink(content.contact_whatsapp)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-[#25D366] text-slate-950 hover:bg-[#3fe07a] transition-colors">
                <WhatsAppIcon size={16} /> WhatsApp
              </a>
            )}
            {content.contact_messenger && (
              <a data-testid="contact-messenger-link" href={messengerLink(content.contact_messenger)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-[#0084FF] text-white hover:bg-[#1a90ff] transition-colors">
                <MessengerIcon size={16} /> Messenger
              </a>
            )}
          </div>

        </div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="contact-form"
          onSubmit={submit}
          noValidate
          className="lg:col-span-7 bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 lg:p-10 backdrop-blur-sm space-y-5"
        >
          <Honeypot />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-2 block">{t.contact.form.name}</label>
              <Input
                data-testid="contact-form-name-input"
                data-field="name"
                aria-invalid={!!errors.name}
                value={form.name}
                onChange={set("name")}
                placeholder={t.contact.form.name_ph}
                className={`bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-slate-500 ${errCls(errors.name)}`}
              />
              <FieldError id="contact-name" msg={errors.name} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-2 block">{t.contact.form.email}</label>
              <Input
                data-testid="contact-form-email-input"
                data-field="email"
                aria-invalid={!!errors.email}
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t.contact.form.email_ph}
                className={`bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-slate-500 ${errCls(errors.email)}`}
              />
              <FieldError id="contact-email" msg={errors.email} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-2 block">{t.contact.form.phone}</label>
              <Input
                data-testid="contact-form-phone-input"
                data-field="phone"
                aria-invalid={!!errors.phone}
                value={form.phone}
                onChange={set("phone")}
                placeholder={t.contact.form.phone_ph}
                className={`bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-slate-500 ${errCls(errors.phone)}`}
              />
              <FieldError id="contact-phone" msg={errors.phone} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-2 block">{t.contact.form.topic}</label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="contact-form-service-select" className="bg-white/5 border-white/10 text-white focus:ring-slate-500">
                  <SelectValue placeholder={t.contact.form.topic_ph} />
                </SelectTrigger>
                <SelectContent>
                  {t.contact.options.map((s, i) => (
                    <SelectItem key={s} value={s} data-testid={`service-option-${i}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 mb-2 block">{t.contact.form.message}</label>
            <Textarea
              data-testid="contact-form-message-input"
              data-field="message"
              aria-invalid={!!errors.message}
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder={t.contact.form.message_ph}
              className={`bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-slate-500 resize-none ${errCls(errors.message)}`}
            />
            <FieldError id="contact-message" msg={errors.message} />
          </div>
          <div data-field="consent" tabIndex={-1}>
            <label className="flex items-start gap-3 cursor-pointer text-xs text-slate-400 leading-relaxed">
              <Checkbox
                data-testid="contact-form-consent-checkbox"
                checked={consent}
                onCheckedChange={(v) => { setConsent(v); if (errors.consent) setErrors({ ...errors, consent: undefined }); }}
                className={`mt-0.5 border-white/20 data-[state=checked]:bg-brand-teal data-[state=checked]:border-brand-teal ${errors.consent ? "border-rose-500" : ""}`}
              />
              <span>{t.contact.form.consent}</span>
            </label>
            <FieldError id="contact-consent" msg={errors.consent} />
          </div>
          <GuardNotice />
          <button
            data-testid="contact-form-submit-button"
            type="submit"
            disabled={sending}
            className="w-full sm:w-auto bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-10 py-3.5 rounded-full text-sm transition-colors duration-200 inline-flex items-center justify-center gap-2"
          >
            {sending && <Loader2 size={16} className="animate-spin" />}
            {sending ? t.contact.form.sending : t.contact.form.submit}
          </button>
        </motion.form>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        data-testid="contact-map"
        className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 mt-16 lg:mt-24"
      >
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400 font-semibold"><span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-teal mr-3 align-middle" />{t.contact.map_overline}</p>
            <h3 className="font-display font-bold tracking-tight text-xl sm:text-2xl mt-2">{t.contact.map_title}</h3>
            <p className="text-slate-400 text-sm mt-2">{t.contact.map_sub}</p>
          </div>
          <a
            data-testid="contact-map-route-link"
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(content.contact_address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 self-start sm:self-auto border border-white/15 hover:bg-white/10 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors duration-200"
          >
            <Navigation size={15} />
            {t.contact.map_route}
          </a>
        </div>
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-slate-800">
          <iframe
            data-testid="contact-map-iframe"
            title={t.contact.map_iframe_title}
            src={`https://maps.google.com/maps?q=${encodeURIComponent("52.183073,21.015891 (Twoje Biuro — Domaniewska 17/19)")}&z=16&ie=UTF8&iwloc=&output=embed&hl=${lang}`}
            width="100%"
            height="380"
            style={{ border: 0, display: "block", filter: "grayscale(0.35) contrast(1.05)" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </motion.div>
    </section>
  );
}
